/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable prefer-regex-literals */
import { useEffect, useRef, useState } from 'react';
import { UseFormRegister, UseFormSetValue, useForm } from 'react-hook-form';
import cn from 'classnames';
import { DaySelector } from './components/DaySelector/DaySelector';
import { MonthSelector } from './components/MonthSelector/MonthSelector';
import styles from './DatePicker.module.scss';
import { YearSelector } from './components/YearSelector/YearSelector';
import { Input } from '../Input/Input';
import { Button } from '../Button/Button';
import { YearPicker } from './components/YearSelector/YearPicker';
import { MonthPicker } from './components/MonthSelector/MonthPicker';

export type DatePickerDayStyle =
  | 'success'
  | 'warning'
  | 'error'
  | 'disabled'
  | 'normal';

interface DatePickerProps {
  register?: UseFormRegister<any>;
  setValue?: UseFormSetValue<any>;
  name?: string;
  label?: string;
  firstDateAvailable?: Date;
  defaultDate?: Date;
  lastDateAvailable?: Date;
  personalizeDays?: DatePickerDayStyle[];
  onMonthChange?: (date: string) => void;
  onDayChange?: (date: string) => void;
}

interface DatePickerForm {
  DD: string;
  MM: string;
  YYYY: string;
}

export const DatePicker = ({
  register,
  setValue,
  name,
  label,
  defaultDate = new Date(),
  firstDateAvailable = new Date('02/05/2024'),
  lastDateAvailable = new Date('03/06/2024'),
  personalizeDays = ['error', 'warning'],
  onMonthChange = (date) => {},
  onDayChange = (date) => {},
}: DatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<'DAY' | 'MONTH' | 'YEAR'>('DAY');
  const inputContainer = useRef<HTMLDivElement>(null);
  const pickerContainer = useRef<HTMLDivElement>(null);
  register(name);
  const [maxDayInMonth, setMaxDayInMonth] = useState<number>(31); // Max Day in current month

  // Start Date states
  const [DD, setDD] = useState<string>(
    String(defaultDate.getDate()).padStart(2, '0')
  );
  const [MM, setMM] = useState<string>(
    String(defaultDate.getMonth() + 1).padStart(2, '0')
  );
  const [YYYY, setYYYY] = useState<string>(String(defaultDate.getFullYear()));
  // End Date states

  // Start first date available parsing
  const FIRST_DATE_AVAILABLE_YEAR = firstDateAvailable.getFullYear();
  const FIRST_DATE_AVAILABLE_MONTH = firstDateAvailable.getMonth() + 1;
  // End first date available parsing

  // Start Last Day Available
  const LAST_DATE_AVAILABLE_YEAR = lastDateAvailable.getFullYear();
  const LAST_DATE_AVAILABLE_MONTH = lastDateAvailable.getMonth() + 1;
  const LAST_DATE_AVAILABLE_DAY = lastDateAvailable.getDate();
  // End Last Day Available

  const {
    register: registerInternal,
    setValue: setInternalValue,
    getValues: getInternalValues,
    setFocus,
    watch,
  } = useForm<DatePickerForm>();
  watch();
  useEffect(() => {
    console.log(123);

    const maxDayInM = new Date(`${MM}-01-${YYYY}`);
    maxDayInM.setMonth(maxDayInM.getMonth() + 1);
    maxDayInM.setDate(0);

    setMaxDayInMonth(maxDayInM.getDate());

    if (
      Number(DD) > maxDayInM.getDate() &&
      MM.length === 2 &&
      YYYY.length === 4
    ) {
      const realMaxDay =
        maxDayInM.getDate() > LAST_DATE_AVAILABLE_DAY
          ? LAST_DATE_AVAILABLE_DAY
          : maxDayInM.getDate();
      setInternalValue('DD', String(realMaxDay));
      setDD(String(realMaxDay));
    }
    setValue(
      name,
      `${getInternalValues('DD')}/${getInternalValues(
        'MM'
      )}/${getInternalValues('YYYY')}`
    );
  }, [
    DD,
    MM,
    YYYY,
    setInternalValue,
    setValue,
    name,
    LAST_DATE_AVAILABLE_DAY,
    getInternalValues('DD'),
    getInternalValues('MM'),
    getInternalValues('YYYY'),
    getInternalValues,
  ]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const rect = pickerContainer.current.getBoundingClientRect();

      const clickX = event.clientX;
      const clickY = event.clientY;

      const isInside =
        clickX >= rect.left &&
        clickX <= rect.right &&
        clickY >= rect.top &&
        clickY <= rect.bottom;

      if (!isInside) setIsOpen(false);
    };
    if (pickerContainer.current && inputContainer.current) {
      document.getElementById('root').addEventListener('click', handleClick);
    }

    return () => {
      document.getElementById('root').removeEventListener('click', handleClick);
    };
  }, [isOpen, inputContainer, pickerContainer]);

  const evalDateInterval = (
    dateToEval: string,
    maxDateLength: number,
    min: number,
    max: number,
    formName: 'DD' | 'MM' | 'YYYY',
    onChangeVariation?: () => void
  ) => {
    const setPrevValue = (internalFormName: typeof formName, value: string) => {
      switch (internalFormName) {
        case 'DD':
          setDD(value);
          break;
        case 'MM':
          setMM(value);
          break;
        case 'YYYY':
          setYYYY(value);
          break;
      }
    };

    const getPrevValue = (internalFormName: typeof formName) => {
      switch (internalFormName) {
        case 'DD':
          return DD;
        case 'MM':
          return MM;
        case 'YYYY':
          return YYYY;
        default:
          return '';
      }
    };

    const getNextFocus = (internalFormName: typeof formName) => {
      switch (internalFormName) {
        case 'DD':
          return 'MM';
        case 'MM':
          return 'YYYY';
        case 'YYYY':
          return 'YYYY';
        default:
          return 'DD';
      }
    };

    const dateLength = dateToEval.length;
    const DATE_NUMBER = Number(dateToEval);

    if (
      (Number(dateToEval) < 1 && dateLength === maxDateLength) ||
      isNaN(DATE_NUMBER)
    ) {
      setInternalValue(formName, getPrevValue(formName));
      return;
    }

    if (maxDateLength === dateLength) {
      setFocus(getNextFocus(formName));
    }

    if (Number(dateToEval) < min && dateLength === maxDateLength) {
      setInternalValue(formName, String(min));
      setPrevValue(formName, String(min));
      return;
    }

    if (Number(dateToEval) > max && dateLength === maxDateLength) {
      setInternalValue(formName, String(max));
      setPrevValue(formName, String(max));
      return;
    }

    if (dateLength > maxDateLength) {
      setInternalValue(formName, getPrevValue(formName));
      setPrevValue(formName, getPrevValue(formName));

      if (getNextFocus(formName) !== formName) {
        setFocus(getNextFocus(formName));
        setInternalValue(getNextFocus(formName), dateToEval[dateLength - 1]);
        setPrevValue(getNextFocus(formName), dateToEval[dateLength - 1]);
      }

      return;
    }
    onChangeVariation?.();
    setPrevValue(formName, dateToEval);
  };

  const renderSelector = () => {
    if (view === 'YEAR') {
      return (
        <YearPicker
          startDate={firstDateAvailable}
          endDate={lastDateAvailable}
          year={YYYY}
          setYear={setYYYY}
          setView={setView}
        />
      );
    }

    if (view === 'MONTH') {
      return (
        <MonthPicker
          startDate={firstDateAvailable}
          endDate={lastDateAvailable}
          year={YYYY}
          setMonth={setMM}
          setYear={setYYYY}
          month={MM}
          setView={setView}
        />
      );
    }

    if (view === 'DAY') {
      return (
        <>
          <YearSelector
            onPrev={() => setYYYY((Number(YYYY) - 1).toString())}
            onNext={() => setYYYY((Number(YYYY) + 1).toString())}
            onYear={() => {
              setView('YEAR');
            }}
            year={YYYY}
            setYear={setYYYY}
            minMaxYear={[FIRST_DATE_AVAILABLE_YEAR, LAST_DATE_AVAILABLE_YEAR]}
          />
          <MonthSelector
            month={MM}
            year={YYYY}
            minMaxYear={[FIRST_DATE_AVAILABLE_YEAR, LAST_DATE_AVAILABLE_YEAR]}
            minMaxMonth={[
              FIRST_DATE_AVAILABLE_MONTH,
              LAST_DATE_AVAILABLE_MONTH,
            ]}
            onMonth={() => {
              setView('MONTH');
            }}
            onNext={() => {
              const newDate = new Date(`${MM}-${DD}-${YYYY}`);
              newDate.setMonth(newDate.getMonth() + 1);
              const newMonth = (newDate.getMonth() + 1)
                .toString()
                .padStart(2, '0');
              const newYear = newDate.getFullYear().toString();

              setMM(newMonth);
              setYYYY(newYear);
              onMonthChange?.(`${newMonth}-01-${newYear}`);
            }}
            onPrev={() => {
              const newDate = new Date(`${MM}-${DD}-${YYYY}`);
              const needsToChangeYear = newDate.getMonth() === 0;
              newDate.setMonth(
                needsToChangeYear ? newDate.getMonth() - 1 : newDate.getMonth()
              );
              const newMonth = needsToChangeYear
                ? newDate.getMonth() + 1
                : newDate.getMonth();
              setMM(newMonth.toString().padStart(2, '0'));
              setYYYY(newDate.getFullYear().toString());
              onMonthChange?.(`${newMonth}-01-${newDate.getFullYear()}`);
            }}
          />
          <DaySelector
            YYYY={YYYY}
            MM={MM}
            DD={DD}
            minDate={firstDateAvailable}
            maxDate={lastDateAvailable}
            daysStyle={personalizeDays}
            selectedMM={getInternalValues('MM')}
            selectedYYYY={getInternalValues('YYYY')}
            onClick={(event, day) => {
              setDD(day);
              setInternalValue('DD', day);
              setInternalValue('MM', MM);
              setInternalValue('YYYY', YYYY);
              onDayChange?.(`${MM}-${day}-${YYYY}`);
            }}
          />
        </>
      );
    }

    return <div />;
  };

  return (
    <div className={styles.root}>
      {label && <label>{label}</label>}
      <div ref={inputContainer} className={styles['input-container']}>
        <Input
          readOnly
          register={registerInternal('DD', {
            onChange: (event) =>
              evalDateInterval(event.target.value, 2, 1, maxDayInMonth, 'DD'),
          })}
          name={name}
          classname={cn(styles.input, styles.dd)}
          placeholder="DD"
          type="number"
        />
        <span className={styles.slash}>/</span>
        <Input
          readOnly
          register={registerInternal('MM', {
            onChange: (event) =>
              evalDateInterval(event.target.value, 2, 1, 12, 'MM', () => {
                if (YYYY !== '' && LAST_DATE_AVAILABLE_YEAR === Number(YYYY)) {
                  setInternalValue(
                    'MM',
                    LAST_DATE_AVAILABLE_MONTH > Number(MM)
                      ? LAST_DATE_AVAILABLE_MONTH.toString().padStart(2, '0')
                      : MM
                  );
                }
              }),
          })}
          name={name}
          classname={cn(styles.input, styles.mm)}
          onKeyDown={(data) => {
            if (
              data.code === 'Backspace' &&
              data.currentTarget.value.length < 1
            ) {
              setInternalValue('MM', '');
              setMM('');
              setTimeout(() => {
                setFocus('DD', { shouldSelect: true });
              }, 0);
            }
          }}
          onFocus={() => {
            if (
              getInternalValues('DD') === '' ||
              getInternalValues('DD').length < 2
            ) {
              setFocus('DD');
            }
          }}
          placeholder="MM"
        />
        <span className={styles.slash}>/</span>
        <Input
          readOnly
          register={registerInternal('YYYY', {
            onChange: (event) =>
              evalDateInterval(
                event.target.value,
                4,
                FIRST_DATE_AVAILABLE_YEAR,
                LAST_DATE_AVAILABLE_YEAR,
                'YYYY'
              ),
          })}
          name={name}
          classname={cn(styles.input, styles.yyyy)}
          placeholder="YYYY"
          onKeyDown={(data) => {
            if (
              data.code === 'Backspace' &&
              data.currentTarget.value.length < 1
            ) {
              setInternalValue('YYYY', '');
              setYYYY('');
              setTimeout(() => {
                setFocus('MM', { shouldSelect: true });
              }, 0);
            }
          }}
          onFocus={() => {
            if (
              getInternalValues('MM') === '' ||
              getInternalValues('MM').length < 2
            ) {
              setFocus('MM');
            }

            if (
              getInternalValues('DD') === '' ||
              getInternalValues('DD').length < 2
            ) {
              setFocus('DD');
            }
          }}
        />
        <Button
          className={styles['date-button']}
          onClick={() => setIsOpen((open) => !open)}
          iconPlace="only-icon"
          icon="calendar"
        />
      </div>
      {isOpen && (
        <div ref={pickerContainer} className={styles.date}>
          {renderSelector()}
        </div>
      )}
    </div>
  );
};
